import { useEntitlements } from '@/hooks/useEntitlements';

import {
  NotificationSettingsCards,
  SettingContentCard,
  SettingSection,
  SettingSectionHeader,
} from '@/sections/settings/components';
import { ReportPreferencesCard } from '@/sections/settings/components/cards/ReportPreferencesCard';

/**
 * NotificationsTab manages notification integrations, exposure alerts, and webhook configuration.
 *
 * @remarks
 * This component handles:
 * 1. **Notification integrations** - Connected services (Slack, Teams, Jira, etc.)
 * 2. **Exposure alerts** - Port/service exposure monitoring
 * 3. **Webhook URL** - Webhook endpoint for external data push
 * 4. **Scheduled Reports** - Automated security health report delivery
 *
 * Architecture follows ScanSettingsTab pattern:
 * - Simple tab wrapper (~60 lines)
 * - Complex logic in NotificationSettingsCards component
 * - Single unified card containing all notification settings
 *
 * React 19 Features:
 * - useOptimistic: Instant integration disconnect with automatic rollback
 * - Suspense: Progressive loading for webhook section
 * - ErrorBoundary: Graceful error handling
 *
 * @example
 * ```tsx
 * // Used in Settings page tabs
 * {selectedTab === 'notificationSettings' && <NotificationsTab />}
 * ```
 *
 * @see {@link NotificationSettingsCards} Core notification settings component
 * @see {@link ScanSettingsTab} Reference pattern implementation
 */
export function NotificationsTab() {
  const { canManageSettings } = useEntitlements();

  return (
    <>
      {/* Notification Settings Section */}
      <div className="flex flex-col gap-6">
        <SettingSectionHeader
          title="Notification Settings"
          description="Manage notification integrations, exposure alerts, and webhook endpoints for your organization."
        />

        {/* Unified Card with All Notification Settings */}
        <SettingContentCard data-testid="notification-settings-card">
          <NotificationSettingsCards editable={canManageSettings} />
        </SettingContentCard>
      </div>

      {/* Scheduled Reports Section */}
      <SettingSection
        title="Scheduled Reports"
        description="Configure automated security health report delivery."
        cardPadding="sm"
      >
        <ReportPreferencesCard editable={canManageSettings} />
      </SettingSection>
    </>
  );
}
